import { useEffect, useMemo, useState } from "react";
import {
  MaterialReactTable,
  useMaterialReactTable,
  MRT_ShowHideColumnsButton,
  MRT_ToggleFullScreenButton,
  MRT_ToggleGlobalFilterButton,
  MRT_GlobalFilterTextField,
  MRT_FilterOptionMenu,
  MRT_ToggleFiltersButton,
} from "material-react-table";
import { mkConfig, generateCsv, asBlob } from "export-to-csv";
import { Box, Button, IconButton, Typography, Card } from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import { AddCircle, Backup, AddCircleOutline } from "@mui/icons-material";
import CustomButton from "./CustomButton";
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import { useLocation } from "react-router-dom";
import { hasCreatePermission, hasEditPermission, hasDeletePermission, handleExportPermission } from '../../services/CommonServices';
import { t } from "../../constants/translationHelper";
import { printFunction } from "../../services/apiServices";
import CustomPrintModel from "./CustomPrint";
import CustomReportPrint from "./CustomReportPrint";
import { handleExportData } from "./handleExportData";


const CustomDataTable = ({ title, buttonTitle, columns, data, fromDate = "", toDate = "", handleOpen, handleFilterButton, fiterButtonTitile,
  tableStyle, enableExpanding, enableExpandAll, getRowCanExpand, renderDetailPanel, footerCellStyle,
  rowCount,
  page,
  setPage,
  pageSize,
  setPageSize,
}) => {
  const [permission, setPermissions] = useState()
  const csvConfig = mkConfig({
    fieldSeparator: ",",
    decimalSeparator: ".",
    useKeysAsHeaders: true,
  });
  const location = useLocation();
  const currentPath = location.pathname;

  //  Define print filename
  const fileName = `${t(title)}.pdf`;

  //  Define print function
  const printContent = () => {
    const elementId = 'contentId';
    const iframeId = 'print-iframe';

    // Ensure element exists before printing
    const contentElement = document.getElementById(elementId);
    if (!contentElement) {
      console.error("Print content element not found!");
      return;
    }

    // Call your utility print function
    printFunction(elementId, iframeId, fileName);
  };

  //  Define content provider for CustomPrintModel
  // Inside CustomDataTable.js
  const getPrintContent = () => {
    return <CustomReportPrint
      title={title}
      columns={columns}
      tableData={data}
      fromDate={fromDate}
      toDate={toDate}

    />;
  };
  // ===================== END PRINT IMPLEMENTATION ===================== 
  useEffect(() => {
    let rolePermisionsData = JSON.parse(sessionStorage.getItem('userData')) || []
    // let mappedPath = currentPath.replace("/alexxcansols", "/app");
    const mappedPath = currentPath.replace(
      /^\/(alexxcansols|app|partner)/,
      "/app"
    );

    // let createPermision = hasCreatePermission(currentPath, rolePermisionsData?.permissions)
    let createPermision = hasCreatePermission(mappedPath, rolePermisionsData?.permissions)
    let editPermision = hasEditPermission(mappedPath, rolePermisionsData?.permissions)
    let deletePermission = hasDeletePermission(mappedPath, rolePermisionsData?.permissions)
    let exportPermission = handleExportPermission(mappedPath, rolePermisionsData?.permissions)

    setPermissions({
      create: createPermision,
      edit: editPermision,
      delete: deletePermission,
      export: exportPermission
    })
  }, [])

  const translatedColumns = useMemo(
    () =>
      columns.map(col => ({
        ...col,
        header: col.header ? t(col.header) : col.header,
      })),
    [columns, t]
  );


  const table = useMaterialReactTable({
    // columns,
    columns: translatedColumns,
    data: data,
    fromDate: fromDate || "",
    toDate: toDate || "",
    // @@@@@@@@@@@@@@@@@@@@@ start pagination
    manualPagination: true, // ✅ server-side pagination
    rowCount, // ✅ total count from API
    state: {
      pagination: { pageIndex: page, pageSize },
    },
    enableColumnFilter: false,
    enableSorting: false,
    // ✅ Update pagination states on change
    muiPaginationProps: {
      rowsPerPageOptions: [
        10,
        100,
        500,
        1000,
        10000,
        { label: "All", value: rowCount },
      ],
    },

    onPaginationChange: (updater) => {
      let newPagination =
        typeof updater === "function"
          ? updater({ pageIndex: page, pageSize })
          : updater;

      const selectedSize = newPagination.pageSize;
      if (selectedSize === rowCount) {
        setPageSize(rowCount);
        setPage(0);
      } else {
        setPageSize(selectedSize);
        setPage(newPagination.pageIndex);
      }
    },

    // @@@@@@@@@@@@@@@@@@@@@ end pagination
    positionGlobalFilter: 'none',
    enableStickyHeader: true,
    enableColumnFilter: false,
    enableColumnActions: false,
    enableSorting: true,
    enableStickyFooter: true,
    muiTableFooterRowProps: {
      sx: {
        backgroundColor: '#e1dddd69',
      },
    },

    muiTableFooterCellProps: ({ column }) => ({
      sx: {
        padding: '10px',
        fontWeight: 600,
        fontSize: '12px',
        textAlign: "center",
        ...footerCellStyle,
        ...column.columnDef.footerCellStyle,
      },
    }),


    muiTableHeadCellProps: ({ column }) => ({
      sx: {
        fontSize: '14px',
        background: '#e1dddd69',
        textTransform: 'capitalize',
        justifyContent: 'center',
        textAlign: 'center',
        alignItems: 'center',
        padding: "5px",

        "& .css-1w86f15": {
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          width: "100%",
          ...column.columnDef.tableHeader,

        },

      },
    }),

    muiTableBodyProps: {
      //simple styling with the `sx` prop, works just like a style prop in this example
      sx: {
        fontWeight: 'normal',
        fontSize: '14px',
        textTransform: 'capitalize',
      },
    },
    muiTableBodyCellProps: ({ column }) => ({
      sx: {
        ...column.columnDef.tableStyle,
        padding: "5px",
        whiteSpace: 'nowrap',
      },
    }),


    //move the search box to the left of the top toolbar
    renderTopToolbarCustomActions: () => (
      <Box
        sx={{
          display: "flex",
          gap: "16px",
          justifyContent: "space-between",
          padding: "8px",
          flexWrap: "wrap",
          width: "100%",
          alignItems: "center",
        }}
      >
        <Typography sx={{ fontWeight: 600, textTransform: "capitalize" }}>
          {t(title)}
        </Typography>
      </Box>
    ),
    renderToolbarInternalActions: ({ table }) => {
      return (
        <>
          {/* Printable hidden content */}
          <CustomPrintModel contentId="contentId">
            {getPrintContent()}
          </CustomPrintModel>

          <MRT_GlobalFilterTextField table={table} style={style} />
          <MRT_ToggleGlobalFilterButton table={table} sx={{ fontSize: '20px' }} />

          <MRT_ShowHideColumnsButton table={table} sx={{ fontSize: '20px' }} />

          <MRT_ToggleFiltersButton table={table} sx={{ fontSize: '20px' }} />
          <MRT_ToggleFullScreenButton table={table} sx={{ fontSize: '20px' }} />
          {permission?.export &&
            <IconButton
              onClick={printContent}
            >
              <PrintOutlinedIcon sx={{ fontSize: '20px' }} />
            </IconButton>
          }
          {permission?.export &&
            <IconButton onClick={() => handleExportData(data, columns, title, fromDate, toDate)}>
              <FileDownloadOutlinedIcon sx={{ fontSize: '20px' }} />
            </IconButton>
          }
          {fiterButtonTitile && (
            <CustomButton
              startIcon={<FilterAltOutlinedIcon />}
              // title={fiterButtonTitile}
              title={fiterButtonTitile ? t(fiterButtonTitile) : ""}
              size="small"
              variant="outlined"
              onClick={() => handleFilterButton(data)}
              sx={{ minWidth: 'auto', whiteSpace: 'nowrap' }}
            />
          )}
          {buttonTitle && permission?.create && (
            <CustomButton
              startIcon={<AddOutlinedIcon />}
              // title={buttonTitle}
              title={buttonTitle ? t(buttonTitle) : ""}
              size="small"
              variant="contained"
              onClick={handleOpen}
              sx={{ minWidth: 'auto', whiteSpace: 'nowrap' }}
            />
          )}

        </>
      );
    },
    muiTablePaperProps: {
      elevation: 0
    },
    enableExpanding: enableExpanding,
    enableExpandAll: enableExpandAll,
    getRowCanExpand: () => getRowCanExpand,
    renderDetailPanel: renderDetailPanel,

    // paginateExpandedRows={false}

  });
  return (
    <>
      <MaterialReactTable
        table={table}
        columnFilterDisplayMode="popover"
      />
    </>
  );
};

export default CustomDataTable;

const style = {
  width: 200,
  // border: '1px solid #CED4DA',
  borderRadius: '4px',
  // paddingLeft: '10px',
  height: '35px',
  marginTop: '4px',
  fontFamily: 'Poppins, sans-serif !important',
  color: '#495057',
  fontSize: '14px',
  '& .MuiOutlinedInput-root': {
    borderRadius: '4px',
    height: '35px',
    '& fieldset': {
      border: 'none',
    },
    '&:hover fieldset': {
      border: 'none',
    },
    '&.Mui-focused fieldset': {
      border: 'none',
    },
  },
  '& .MuiInputBase-input': {
    padding: '0 0',
  },
};
